import { inject, Injectable, EventEmitter } from '@angular/core';
import { DataService } from './data.service';
import { FilterProvider, FilterExpression, TransformationService, StorageService, SelectionService, ConditionExpression, IConditionExpression, WorkflowService, WorkflowDocument, MigrationService, effectiveStatus, QUICK_FILTER_NAME } from '@luzula/luzula-lib';
import { Values, ValueExtractors } from './bohrpunkte/filter/application.filter';
import { isLightweightField } from './bohrpunkte/filter/distinct-values.util';
import { ProjectService } from './project.service';
import { AuthService } from './auth.service';
import { isForeignWipHidden } from './bohrpunkte/detail/point-project-membership';

@Injectable({
	providedIn: 'root'
})
export class FilterService implements FilterProvider {
	private static readonly k = 'conditionExpressionObjects';
	filtersUpdated = new EventEmitter<void>();

	apply = new EventEmitter<void>();

	/**
	 * „Drill-down"-Ansicht aus einer Auswertung: das Listen-Panel soll die Liste
	 * auf genau diese Punkt-IDs filtern (benannter Ad-hoc-Filter `label` → „×"-Reset
	 * + Auswahl-Kopplung wie bei jedem Filter). Das Panel hört darauf, weil nur es
	 * `currentFilterName` + Auswahl konsistent setzen kann.
	 */
	applyAdHocView = new EventEmitter<{ label: string; ids: string[] }>();

	filteredDocIds: Set<string> = new Set<string>();
	currentFilterFunc?: (doc: any) => boolean;

	/**
	 * Setzt die gefilterten IDs direkt (für Projekt-Filterung client-seitig)
	 */
	setFilteredDocIds(ids: Set<string> | string[]) {
		this.filteredDocIds = ids instanceof Set ? ids : new Set(ids);
		this.apply.emit();
	}

	// Aktuelle Filter-Bounds (null = keine Bounds-Einschränkung)
	// Verwendet eigenes Interface statt Leaflet.Point, da nur x/y benötigt werden
	private filterBounds: { l: { x: number, y: number }, u: { x: number, y: number } } | null = null;

	private ds = inject(DataService);
	private storage = inject(StorageService);
	private ts = inject(TransformationService);
	private selectionService = inject(SelectionService);
	private workflow = inject(WorkflowService);
	private migrationService = inject(MigrationService);
	private projectService = inject(ProjectService);
	private auth = inject(AuthService);

	// Cache für getDistinctValues — pro Feld eine Promise. Wird bei Datenänderung
	// (checkedIn/checkedOut/discarded) verworfen, damit neue Werte sichtbar werden.
	private distinctValuesCache = new Map<string, Promise<{ value: string; count: number; label?: string }[]>>();

	// Cache der schlanken View-Rows (ohne include_docs) als rekonstruierte
	// Light-Dokumente. Quelle des schnellen Pfads; wird mit dem Wert-Cache verworfen.
	private lightDocsCache: Promise<any[]> | null = null;

	constructor() {
		this.updateConditionExpressionObjects();
		this.ds.checkedOut.subscribe(() => { this.invalidateDistinctValues(); this.updateConditionExpressionObjects(); });
		this.ds.checkedIn.subscribe(() => { this.invalidateDistinctValues(); this.updateConditionExpressionObjects(); });
		this.ds.discarded.subscribe(() => { this.invalidateDistinctValues(); this.updateConditionExpressionObjects(); });
	}

	private invalidateDistinctValues(): void {
		this.distinctValuesCache.clear();
		this.lightDocsCache = null;
	}

	updateConditionExpressionObjects() {
		this.ds.getFilter()?.then((doc: any) => {
			const views = doc.views;
			const conditionExpressionObjects: any = {};
			for (const [key, value] of Object.entries(views)) {
				const v = value as any;
				if (key.endsWith('_checkout')) continue;
				if (!v.object) continue;
				conditionExpressionObjects[key] = (value as any).object;
			}
			this.storage.set(FilterService.k, conditionExpressionObjects).then(() => this.filtersUpdated.emit());
		})
	}
	
	async set(key: string, value: FilterExpression) {
		let conds = await this.storage.get(FilterService.k) as any;
		if (!conds) {
			conds = {};
		}
		conds[key] = value.toObject();
		await this.storage.set(FilterService.k, conds);
		this.filtersUpdated.emit();
	}

	async list_keys() {
		const conds = await this.storage.get(FilterService.k);
		if (!conds) return [];
		// Den reservierten Schnellfilter-Schluessel nicht als "gespeicherten" Filter
		// listen — er ist der unbenannte Ad-hoc-Filter aus dem Dialog.
		return Object.keys(conds).filter(k => k !== QUICK_FILTER_NAME);
	}

	async get(key: string): Promise<ConditionExpression | undefined> {
		const conds = await this.storage.get(FilterService.k) as any;
		if (!conds) return undefined;
		const condExp = conds[key] as IConditionExpression;
		if (!condExp || !condExp.type) return undefined;
		return ConditionExpression.conditionExpression(condExp);
	}
	
	getAll() {
		return this.storage.get(FilterService.k) as any;
	}
	
	async remove(key: string) {
		const conds = await this.storage.get(FilterService.k) as any;
		if (!conds) return;
		delete conds[key];
		await this.storage.set(FilterService.k, conds);
	}

	/**
	 * Setzt die Bounds für den nächsten Filter-Aufruf.
	 * Bei null wird ohne Bounds-Einschränkung gefiltert.
	 */
	setFilterBounds(bounds: { l: { x: number, y: number }, u: { x: number, y: number } } | null): void {
		this.filterBounds = bounds;
	}

	/**
	 * Wendet den Filter an - holt die gefilterten IDs aus der View.
	 * storeViews muss vorher aufgerufen worden sein!
	 */
	async applyFilter(name: string) {
		// View abrufen und gefilterte IDs speichern
		try {
			const docs = await this.ds.getView(name);
			this.filteredDocIds.clear();
			if (docs?.rows) {
				docs.rows.forEach((d: any) => {
					this.filteredDocIds.add(d.key);
				});
			}
			this.apply.emit();
		} catch (reason) {
			console.error(reason);
		}
	}

	/**
	 * Setzt die Filterfunktion ohne DB-Operationen (für client-seitige Filterung)
	 */
	setFilterFunc(exp: FilterExpression): void {
		const func = exp.filterFunction(false, false, []);
		this.currentFilterFunc = (doc: any): boolean => {
			let f: (doc: any) => boolean;
			eval(`f = ${func}`);
			return f!(doc);
		};
	}

	/**
	 * Wendet den Filter client-seitig auf die übergebenen Dokumente an.
	 * Schneller als DB-basierte Filterung.
	 *
	 * Workflow-Sichtbarkeit wird zusätzlich angewandt: Punkte, die der
	 * aktuelle Nutzer laut seiner Rolle nicht sehen darf, fallen aus dem
	 * Ergebnis heraus.
	 */
	applyFilterClientSide(docs: any[]): void {
		this.filteredDocIds.clear();
		const userFilter = this.currentFilterFunc;
		docs.forEach(doc => {
			if (!this.workflow.canSeePoint(doc as WorkflowDocument)) return;
			if (!this.passesProjectVisibility(doc as WorkflowDocument)) return;
			if (userFilter && !userFilter(doc)) return;
			this.filteredDocIds.add(doc._id);
		});
		this.apply.emit();
	}

	/**
	 * App-seitiges Cross-Projekt-Sicht-Tor (ergänzt das Lib-Gate
	 * `WorkflowService.canSeePoint`): blendet laufende (WIP-)Punkte aus, die zu
	 * einem ANDEREN Projekt gehören — Mitgliedschaft via `header.projects`. Das
	 * Lib-Gate kennt nur `workflow.projectId`; frisch angelegte Punkte tragen die
	 * (noch) nicht, weshalb der Schutz hier zusätzlich über die App-Mitgliedschaft
	 * greifen muss. Abgeschlossen/Bestand bleiben öffentlich, Entwickler sieht alles.
	 */
	private passesProjectVisibility(doc: WorkflowDocument): boolean {
		const header = (doc as any).header;
		return !isForeignWipHidden({
			projects: header?.projects,
			status: effectiveStatus(doc),
			activeProjectId: this.projectService.project(),
			isDeveloper: this.auth.canIbe('entwickler'),
			// Bug-2-Fix (2026-06-18): Punkte, deren Workflow zum aktiven Projekt
			// gehört, dürfen nicht als „fremdes WIP" ausgeblendet werden, auch wenn
			// header.projects (noch) aufs Altprojekt zeigt (Reaktivierung/Sync-Merge).
			workflowProjectId: doc.workflow?.projectId
		});
	}

	/**
	 * Zentraler Sichtbarkeits-Filter für Punkt-Listen / Karten-Marker.
	 * Listen-Konsumenten reichen ihr `bps`-Array hier durch, um Punkte
	 * zu entfernen, die der aktuelle Nutzer laut Workflow-Rolle nicht
	 * sehen darf (Bestandsdaten ohne Status bleiben sichtbar).
	 */
	applyWorkflowVisibility<T extends WorkflowDocument>(docs: T[]): T[] {
		return docs.filter(d => this.workflow.canSeePoint(d) && this.passesProjectVisibility(d));
	}

	async storeViews(name: string, exp: FilterExpression, withBounds: boolean, bnds: {l: {x: number, y: number}, u: {x: number, y: number}}[], callback: Function) {
		const func = exp.filterFunction(false, withBounds, bnds);
		this.currentFilterFunc = (doc: any): boolean => {
			let f: (doc: any) => boolean;
			eval(`f = ${func}`);
			return f!(doc);
		};
		return this.ds.storeViews(callback, withBounds, bnds);
	}
	
	async resetFilter() {
		await this.storage.remove('currentFilter');
		delete this.currentFilterFunc;
		this.filteredDocIds.clear();
		this.filtersUpdated.emit();
	}

	/**
	 * Liefert die im Datenbestand vorhandenen Werte für ein Filter-Feld
	 * mit Häufigkeit, sortiert. Wird im Filter-Dialog für die Wert-Vorschau genutzt.
	 *
	 * Zweistufig (siehe isLightweightField): Kopf-Felder werden aus den schlanken
	 * View-Rows berechnet (schnell, ohne `include_docs`); Felder, die
	 * in `extendedHeader`/berechneten Strukturen liegen, brauchen die vollen
	 * Dokumente (`getAll()`). Das Ergebnis wird pro Feld gecacht.
	 */
	async getDistinctValues(fieldName: string): Promise<{ value: string; count: number; label?: string }[]> {
		const cached = this.distinctValuesCache.get(fieldName);
		if (cached) return cached;

		const def = (Values as any)[fieldName];
		if (!def) return [];

		const promise = this.buildDistinctValues(def);
		this.distinctValuesCache.set(fieldName, promise);
		promise.catch(() => this.distinctValuesCache.delete(fieldName));
		return promise;
	}

	/**
	 * Wärmt den Wert-Cache vor: lädt die schlanken View-Rows einmal, sodass der
	 * erste Aufruf für ein Kopf-Feld sofort antwortet. Wird beim Öffnen des
	 * Filter-Dialogs im Hintergrund angestoßen (best effort, blockiert nicht).
	 */
	prefetchDistinctValues(): void {
		this.getLightDocs().catch(() => { /* still bei Bedarf erneut versuchen */ });
	}

	private async buildDistinctValues(def: any): Promise<{ value: string; count: number; label?: string }[]> {
		const docs = isLightweightField(def.accessor) ? await this.getLightDocs() : await this.ds.getAll();
		const observed = this.computeDistinctValues(def, docs);
		// Fixe Werte-Liste deklariert? Alle Optionen aufnehmen (mit echten Counts,
		// falls im Datenbestand vorkommend, sonst 0).
		if (Array.isArray(def.options)) {
			const map = new Map(observed.map(o => [o.value, o.count]));
			return (def.options as string[]).map(value => ({ value, count: map.get(value) ?? 0 }));
		}
		return observed;
	}

	/**
	 * Lädt die schlanken View-Rows (ohne include_docs) und rekonstruiert daraus
	 * Light-Dokumente — identisch zu AlleBohrpunkteComponent.initPoints, damit die
	 * Vorschau dieselben Werte zeigt, die Liste/Suche anzeigen. Gecacht pro Sitzung.
	 */
	private getLightDocs(): Promise<any[]> {
		if (this.lightDocsCache) return this.lightDocsCache;
		const promise = (async () => {
			await this.ds.waitForInitialization();
			const result: any = await this.ds.queryIndex(); // ohne include_docs → Header in key[4]
			const rows: any[] = result?.rows ?? [];
			const docs: any[] = [];
			for (const row of rows) {
				const doc = this.reconstructLightDoc(row);
				if (doc) docs.push(doc);
			}
			return docs;
		})();
		this.lightDocsCache = promise;
		promise.catch(() => { this.lightDocsCache = null; });
		return promise;
	}

	private reconstructLightDoc(row: any): any | null {
		if (!Array.isArray(row?.key)) return null; // _design-/Sonder-Rows überspringen
		const mesoklima = row.key[7];
		const stfgData = row.key[8];
		const extendedHeader: any = (mesoklima || stfgData)
			? {
				mesoklima: mesoklima || undefined,
				stfg: stfgData ? { naehrkraftStufe: stfgData.nk, stammFeuchteStufe: { first: stfgData.sf1, second: stfgData.sf } } : undefined
			}
			: undefined;
		let bp: any = {
			_id: row.id, type: row.key[1], header: row.key[4],
			state: row.key[5], version: row.key[6], extendedHeader, workflow: row.key[9] ?? undefined
		};
		try { bp = this.migrationService.migrate(bp); } catch { /* Doc bleibt unverändert */ }
		return bp;
	}

	private computeDistinctValues(def: any, docs: any[]): { value: string; count: number; label?: string }[] {
		const counts = new Map<string, number>();
		const path: string[] = (def.accessor || '').split('.').filter((p: string) => p.length > 0);
		const extractor = def.array && def.anyOfName ? ValueExtractors[def.anyOfName] : null;
		const convert = def.convert as ((x: any) => any) | undefined;

		const add = (v: any) => {
			if (v == null) return;
			const s = String(v);
			if (s === '' || s === 'undefined' || s === 'null') return;
			counts.set(s, (counts.get(s) ?? 0) + 1);
		};

		for (const doc of docs) {
			// Sonderfall: leerer accessor (z. B. STFG) → convert(doc) direkt
			if (!def.accessor) {
				const v = convert ? convert(doc) : null;
				add(v);
				continue;
			}

			let raw: any = doc;
			for (const seg of path) {
				if (raw == null) break;
				raw = raw[seg];
			}

			if (extractor) {
				const vals = extractor(raw, convert);
				for (const v of vals) add(v);
			} else if (def.array && Array.isArray(raw)) {
				for (const x of raw) add(convert ? convert(x) : x);
			} else {
				add(convert ? convert(raw) : raw);
			}
		}

		const out = Array.from(counts.entries()).map(([value, count]) => ({ value, count }));
		out.sort((a, b) => {
			const an = Number(a.value);
			const bn = Number(b.value);
			if (!isNaN(an) && !isNaN(bn) && a.value.trim() !== '' && b.value.trim() !== '') return an - bn;
			return a.value.localeCompare(b.value, 'de', { numeric: true, sensitivity: 'base' });
		});
		// Optionales Anzeige-Label (z. B. "Beschreibung (Nummer)" fuer Punkttyp).
		// Der gespeicherte/gefilterte Wert bleibt die nackte (konvertierte) Nummer;
		// nur die Vorauswahlliste zeigt das Label. pickerLabel laeuft hier normal
		// (nicht serialisiert) und darf daher die Laufzeit-Listen nutzen.
		const labelFn = def.pickerLabel as ((v: string) => string) | undefined;
		if (typeof labelFn === 'function') {
			return out.map(o => ({ ...o, label: labelFn(o.value) || o.value }));
		}
		return out;
	}
}
